/**
 * Net Checkout PIN service -- the store editor's calls to the distributor-side
 * PIN endpoints (api/src/endpoints/campaign.py: GET/PUT/DELETE
 * /campaign/<id>/net-pin, and PUT /campaign/<id>/net-credential-mode for the
 * P1-1 choice between the store's own PIN and its Shared password).
 *
 * The PIN is write-only: it goes out in a PUT body and is never returned. GET
 * reports STATUS only ({pin_set, pin_set_at, pin_set_by, terms_version,
 * terms_current}).
 *
 * Proposal: docs/planning/2026-10-23-net-checkout-pin-and-gate-decoupling-proposal.md
 */
import api from "./api";

// Mirror password_policy.MIN_NET_PIN_LENGTH / MAX_NET_PIN_LENGTH. The server
// stays the control; the editor uses these to hold a half-typed PIN back.
export const NET_PIN_MIN_LENGTH = 6;
export const NET_PIN_MAX_LENGTH = 64;
export const NET_PIN_SUGGESTED_LENGTH = 8;

// The terms version to acknowledge comes from GET /net-pin
// (terms_version_current); the client keeps no copy of it, so the two can
// never drift.

// PLACEHOLDER copy (proposal 3.7). Jake approves the final wording; change
// the text and bump the version on both sides together.
export const NET_DISTRIBUTOR_TERMS_TEXT =
  "I understand that anyone who enters this PIN can place Net Checkout " +
  "orders on this store, that if shoppers pay by Net I carry the receivable: " +
  "decoration on those orders is invoiced to my business on the 1st and 15th " +
  "whether or not my client pays me, and that I am responsible for who I " +
  "share the PIN with.";

// The same terms (same version) worded for the store-password credential
// (P1-1): there is no PIN, so what the owner shares is the store password.
export const NET_STORE_PASSWORD_TERMS_TEXT =
  "I understand that anyone who has this store's password can place Net " +
  "Checkout orders on this store, that if shoppers pay by Net I carry the " +
  "receivable: decoration on those orders is invoiced to my business on the " +
  "1st and 15th whether or not my client pays me, and that I am responsible " +
  "for who I share the store password with.";

export const NET_CREDENTIAL_MODE_PIN = "pin";
export const NET_CREDENTIAL_MODE_STORE_PASSWORD = "store_password";

export async function getNetPinStatus(storeId) {
  const response = await api.get(`/campaign/${storeId}/net-pin`);
  return response.data;
}

export async function setNetPin(storeId, pin, termsVersion) {
  const response = await api.put(`/campaign/${storeId}/net-pin`, {
    pin,
    terms_version: termsVersion,
  });
  return response.data;
}

export async function clearNetPin(storeId) {
  const response = await api.delete(`/campaign/${storeId}/net-pin`);
  return response.data;
}

/**
 * Choose the store's Net credential (P1-1). ``termsVersion`` is the server's
 * current version and is required for "store_password" (that mode has no PIN
 * row to carry the acknowledgement); it is omitted for "pin". Answers with
 * the same status shape as getNetPinStatus.
 */
export async function setNetCredentialMode(storeId, mode, termsVersion) {
  const body = { mode };
  if (termsVersion !== undefined) body.terms_version = termsVersion;
  const response = await api.put(`/campaign/${storeId}/net-credential-mode`, body);
  return response.data;
}

/**
 * Owner-facing text for a failed call. 429 and 403 get fixed, status-aware
 * copy (their bodies are generic limiter/permission text); anything else
 * shows the server's own refusal, then the fallback. Never the request body
 * (it carries the PIN).
 */
export function netPinErrorMessage(err, fallback) {
  const status = err && err.response && err.response.status;
  if (status === 429) {
    return "Too many attempts. Wait a minute and try again.";
  }
  if (status === 403) {
    return "You do not have permission to change this store's Net Checkout PIN.";
  }
  const data = err && err.response && err.response.data;
  if (data && typeof data.error === "string" && data.error) return data.error;
  if (data && typeof data.message === "string" && data.message) {
    return data.message;
  }
  return fallback;
}
